/**
 * Metafield facets that must not appear in the search filter drawer, read from the
 * `drawer_hidden_filters` theme setting (Search & Discovery params separated by | or new line).
 */
const HIDDEN_DRAWER_FACETS = new Set(
  (Antonioli.filters?.drawer_hidden_filters ?? '')
    .split(/[|\r\n]+/)
    .map((param) => param.replace(/\s/g, ''))
    .filter(Boolean)
    .map((param) => param.replace(/^filter\.p\.m\./, 'meta.'))
);

class SearchFilters {

  constructor() {
    this.activeFacetsContainer = document.querySelector('.active-facets');
    this.params = JSON.parse(document.querySelector('[data-main-search]').dataset.params)
    this.removeFacetsBtn = document.querySelector('.active-facets__button-remove');
    this.search = document.querySelector('[data-main-search]');

    this.getParamValue = (key) => {
      const v = this.params?.[key];
      if (v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'value')) return v.value;
      return v;
    };

    this.getParamLabel = (key) => {
      const v = this.params?.[key];
      if (v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'label')) return v.label;
      return v;
    };

    this.getLabelByAttribute = (attributeValue) => {
      for (const [key, value] of Object.entries(this.params)) {
        if (value && typeof value === 'object') {
          if (value.value === attributeValue) {
            return value.label || attributeValue;
          }
        }
      }
      return attributeValue;
    };

    this.getConfigByAttribute = (attributeValue) => {
      for (const [key, value] of Object.entries(this.params)) {
        if (value && typeof value === 'object') {
          if (value.value === attributeValue) {
            return { key, ...value };
          }
        }
      }
      return null;
    };

    this.filterOrderer = new FilterOrderer();

    this.removeFacetsBtn?.addEventListener('click', () => {
      this.search.clearFilters();
    });


    this.sortByProperties = [
      {
        id: 'published',
        value: 'published',
        text: Antonioli.searchStrings.published_desc,
        order: 'desc'
      },
      {
        id: 'priceMinToMax',
        text: Antonioli.searchStrings.price_asc,
        value: 'price',
        order: 'asc'
      },
      {
        id: 'priceMaxToMin',
        text: Antonioli.searchStrings.price_desc,
        value: 'price',
        order: 'desc'
      },
      {
        id: 'resetValue',
        alignCenter: true,
        text: Antonioli.searchStrings.reset_order,
        value: '',
        order: 'desc'
      }
    ];
  }

  getCurrentFacetsObj(param) {
    return this.search.getCurrentFacetsObj(param);
  }

  applyFilter(element) {
    let attribute = element.getAttribute('attribute');

    const value = element.value ? element.value.toString() : element.getAttribute('value').toString();
    return this.search.applyFilter(attribute, value);
  }

  applySort(value, order) {
    const sortBy = this.search?.querySelector('sort-by');
    sortBy?.toggleAttribute('open', false);
    publish(PUB_SUB_EVENTS.drawerClose, { source: "md-main-search-sorting" });
    return this.search.applySort(value, order);
  }

  applyPriceFilter(element) {
    let attribute = element.getAttribute('attribute');
    const value = element.value.toString();
    return this.search.applyFilter(attribute, encodeURI(value));
  }

  createSelectors(facets) {
    if (this.selectors) return;
    this.selectors = {};
    facets.forEach(facet => {

      switch (facet.attribute) {
        case 'meta.elastick.filter_microcategory':
          this.selectors.microCategoryAttribute = facet.attribute;
          this.selectors.microCategoryDetails = `[data-search-filter-${this.selectors.microCategoryAttribute}]`;
          break;
        case 'meta.elastick.filter_meta_macro':
          this.selectors.macroCategoryAttribute = facet.attribute;
          this.selectors.macroCategoryDetails = `[data-search-filter-${this.selectors.macroCategoryAttribute}]`;
          break;
        case 'meta.department':
          this.selectors.productTypeAttribute = facet.attribute;
          this.selectors.productTypeInputAttribute = `[data-attribute]=${this.selectors.productTypeAttribute}`;
          this.selectors.productTypeDetails = `[data-search-filter="${this.selectors.productTypeAttribute}"]`;
          break;
        default:
          break;
      }

    });

  }

  adjustCounter() {
    let filtersLength = 0
    const obj = this.getCurrentFacetsObj(true)
    Object.keys(obj)
      .forEach(key => {
        if (HIDDEN_DRAWER_FACETS.has(key)) return;
        filtersLength += obj[key]?.length;
      })
    document.querySelectorAll('.SearchFacetsCounter').forEach(el =>
      el.innerHTML = filtersLength > 0 ? ` ( ${filtersLength} )` : ''
    )
  }

  adaptFacets(apiFacets) {
    const facetsArray = [];

    const createFacet = (key, values) => {
      const buckets = Object.entries(values).map(([value, count]) => ({
        value,
        count
      }));

      const configuredLabel = this.getLabelByAttribute(key);
      const finalLabel = configuredLabel || key;
      
      return {
        title: finalLabel, 
        attribute: key,
        buckets
      };
    };

    for (const [key, values] of Object.entries(apiFacets)) {
      if (!values || typeof values !== "object") continue;
      if (key === "tags" || key === "option_names" || key === "meta.elastick.genderweb" || HIDDEN_DRAWER_FACETS.has(key)) continue;
      facetsArray.push(createFacet(key, values));
    }

    return facetsArray;
  }

  handleDefaultFacets(facet) {
    const currentFacets = this.getCurrentFacetsObj(true);

    const order = this.filterOrderer.getOrderStrategy(facet.attribute);
    const buckets = order ? this.filterOrderer.reorderFilters(facet.buckets, order, 'value') : facet.buckets;

    if (!buckets.length || facet.attribute.includes('size')) 
      return;

    const categoryDetails = document.querySelector(this.selectors.productTypeDetails);
    if (currentFacets['product_type'] && currentFacets[this.selectors.productTypeAttribute]) 
      categoryDetails?.classList.add('hide')
    else 
      categoryDetails?.classList.remove('hide')
    

    const facetsWrapper = document.querySelector('[data-facets-wrapper]');

    const [isNew, template] = this.createDetailsTemplate(facet.title, facet.title);

    if (facet.attribute === 'vendor' && !template.querySelector('[data-facet-search]')) {
      template.querySelector('fieldset').prepend(this.addBrandSearch());
    }

    this.printBuckets(template, facet, buckets)
    isNew && facetsWrapper.appendChild(template);
  }

  renderFacets(appFacets, sortBy, clear = false) {
    const facets = this.adaptFacets(appFacets);
    if (clear === true) {
      document.querySelectorAll('[facet-wrapper]').forEach(element => element.remove());
    }
    this.adjustCounter();
    this.createSelectors(facets);
    if (!facets.length > 0) return;

    facets.forEach(facet => {
      switch (facet.attribute) {
        case this.getParamValue('categoryFirstLevel'):
          this.handleDepartments(facet, facets);
          break;
        case this.getParamValue('categorySecondLevel'):
          break;
        case this.getParamValue('categoryThirdLevel'):
          this.handleMicroCategories(facet);
          break;
        case 'price':
          this.handlePrice(facet);
          break;
        default:
          this.handleDefaultFacets(facet);
      }
    });

    this.handleProductTypeFacets(facets);


    const sizes = [
      this.getParamValue('size-clothing'),
      this.getParamValue('size-accessories'),
      this.getParamValue('size-shoes'),
      this.getParamValue('size-bags'),
    ];

    for (const size of sizes) 
      this.handleSizeFacets(facets, size);

    this.removeDetailsWithNoFacets(facets);
    this.renderSortBy(sortBy);
    this.handleAppliedFacets();

    publish(PUB_SUB_EVENTS.facetsUpdate);
  }

  handleMicroCategories(facet) {
    Array.from(document.querySelectorAll(`[data-microcategory]`))
      .forEach(detail => detail.remove())

    const facets = this.getCurrentFacetsObj(true);
    const macroCategories = facets[this.selectors.macroCategoryAttribute];

    if (!macroCategories) return

    const result = macroCategories.reduce((acc, item) => {
      item = Array.isArray(item) ? item[0] : item;
      const [macro, value] = item.split('_').map(str => str.toLowerCase());
      const formattedValue = value.replace(/_/g, ' ');

      const existing = acc.find(obj => obj.macro === macro);

      if (!existing) {
        acc.push({ macro, values: [formattedValue] });
        return acc;
      }

      existing.values.push(formattedValue);
      return acc;
    }, []);
    
    const facetsWrapper = document.querySelector('[data-facets-wrapper]');
    for (let macro of result) {
      for (let value of macro.values) {
        const buckets = facet.buckets.filter(bucket => bucket.value.split('_')[0].toLowerCase() === value);

        const macroDetails = document.querySelector(`[data-search-filter="${macro.macro.toLowerCase()}"]`);

        const [isNew, template] = this.createDetailsTemplate(value, value);
        template.querySelector('[facet-wrapper]').setAttribute('data-microcategory', true)

        this.printBuckets(template, facet, buckets)
        template.querySelector('details').setAttribute('open', true);


        if (macroDetails) {
          macroDetails.insertAdjacentElement('afterend', template.querySelector('[facet-wrapper]'));
          return;
        }
        isNew && facetsWrapper.appendChild(template);
      }
    }
  }

  printBuckets(clonedTemplate, facet, buckets) {
    buckets?.forEach((bucket, index) => {
      if (bucket.param_name === 'separator') {
        index !== buckets.length - 1 && clonedTemplate.querySelector('ul').appendChild(this.filterOrderer.createSeparator());
        return;
      }
      const clonedFacet = this.createFacet(facet, bucket);
      clonedTemplate.querySelector('ul').appendChild(clonedFacet);
    });
  }

  handleDepartments(current, facets) {
    const facetsWrapper = document.querySelector('[data-facets-wrapper]');
    const macroCategory = facets.filter(facet => facet.attribute === this.getParamValue('categorySecondLevel'));

    const availableDepartments = current.buckets.map(b => b.value.toLowerCase());
    document.querySelectorAll('[data-search-filter][data-dept]').forEach(el => {
        if (!availableDepartments.includes(el.getAttribute('data-search-filter'))) {
            el.closest('[facet-wrapper]')?.remove();
        }
    });

    current.buckets?.forEach(bucket => {
      const facetValue = bucket.value.toLowerCase();

      const [isNew, template] = this.createDetailsTemplate(facetValue, facetValue, 'dept');

      template.querySelector('ul').appendChild(this.createFacet(current, bucket));

      macroCategory[0]?.buckets?.forEach(bucket => {
        const parent = bucket.value.split('_')[0].toLowerCase();
        if (parent == facetValue) {
          template.querySelector('ul').appendChild(this.createFacet(macroCategory[0], bucket));
        }
      });
      isNew && facetsWrapper.appendChild(template);
    });
  }

  handlePrice(current) {
    const facetsWrapper = document.querySelector('[data-facets-wrapper]');
    
    const priceLabel = this.getLabelByAttribute('price');
    const [isNew, template] = this.createDetailsTemplate(priceLabel, priceLabel, 'price');

    const lowestValue = current.buckets[0]?.value;
    const highestValue = current.buckets[current.buckets.length - 1]?.value;

    if (!isNew) {
      template.querySelector('[data-price-from]').innerHTML = '';
      template.querySelector('[data-price-to]').innerHTML = '';
    }

    template.querySelector('[data-price-from]').appendChild(this.createPriceFacet(current, lowestValue, 'from'));
    template.querySelector('[data-price-to]').appendChild(this.createPriceFacet(current, highestValue, 'to'));
    isNew && facetsWrapper.appendChild(template);
  }

  handleSizeFacets(allFacets, type) {
    const facets = allFacets.filter(facet => facet.attribute.includes(type));
    let sizeScale = false

    if (!facets.length) {
      const sizeTypeKey = type.replace('-', '');
      document.querySelector(`[data-search-filter="${sizeTypeKey}"]`)?.remove();
      return;
    }

    const sizeScaleParam = this.getParamValue('sizeScale');
    if (sizeScaleParam && sizeScaleParam.filter(scale => scale.type == type).length > 0) {
      sizeScale = sizeScaleParam.filter(scale => scale.type == type).map(scale => scale.values)[0];
    }

    const existScale = facets.filter(facet => facet.attribute.split(type + '-')[1].includes(sizeScale)).length > 0;


    if (!facets.length) return

    const facetsWrapper = document.querySelector('[data-facets-wrapper]');

    const sizeTypeKey = type.replace('-', '');
    const sizeLabel = this.getParamLabel(type);

    const [isNew, template] = this.createDetailsTemplate(sizeLabel, sizeTypeKey, 'size');
    const facetSelect = template.querySelector('select')
    const selectedParam = facetSelect?.dataset?.selectedParam;
    const selectedValue = facetSelect?.getAttribute('data-value');

    const facetsElements = template.querySelector('facets-switcher [data-facets]')

    template.querySelector('facets-switcher [data-switcher]').innerHTML = '';
    if (facetsElements) {
      facetsElements.innerHTML = '';
    }
    const facetSwitcherTemplate = document.getElementById('size-switcher-template');
    const clonedFacetSwitcher = facetSwitcherTemplate.content.cloneNode(true);
    template.querySelector('facets-switcher [data-switcher]').appendChild(clonedFacetSwitcher);

    facets.forEach((facet, index) => {
      const order = this.filterOrderer.getOrderStrategy(facet.title);

      const buckets = order ? this.filterOrderer.reorderFilters(facet.buckets, order, 'value', true) : facet.buckets;
      const facetsTitle = facet.attribute.split(type + '-')[1];

      const facetTemplate = document.getElementById('facets-size-item-template');
      const clonedFacet = facetTemplate.content.cloneNode(true);
      clonedFacet.querySelector('.facet__element').setAttribute('data-facet', facet.attribute);

      let isSelected = false
      if (selectedParam) {
        isSelected = selectedParam === facet.attribute
      } else {
        isSelected = index === 0 && !existScale
      }

      if (isSelected) {
        clonedFacet.querySelector('.facet__element').classList.remove('tw-invisible');
        clonedFacet.querySelector('.facet__element').classList.add('parent-display', 'facets__display-vertical', 'tw-px-[1rem]');
      } else {
        clonedFacet.querySelector('.facet__element').classList.add('tw-invisible');
        clonedFacet.querySelector('.facet__element').classList.remove('parent-display', 'facets__display-vertical', 'tw-px-[1rem]');
      }

      const switcherTemplate = `<option
      data-facet="${facet.attribute}" value="${facetsTitle}">${facetsTitle}</option>`;

      const parsedHTML = new DOMParser().parseFromString(switcherTemplate, 'text/html');
      template.querySelector('select').appendChild(parsedHTML.querySelector('option'));

      buckets.forEach((bucket, index) => {

        if (bucket.param_name === 'separator') {
          index != buckets.length - 1 && clonedFacet.querySelector('fieldset ul').appendChild(this.filterOrderer.createSeparator());
          return;
        }

        let facetValue = bucket.value.toUpperCase();
        const exclude = ['STANDARD', 'INTERNATIONAL'];

        if (facet.title.includes('size-')) {
          let filterLabel = facet.title.split('-').pop().replace('_', '/');
          if (
            filterLabel &&
            !exclude.includes(filterLabel) &&
            !facetValue.includes(filterLabel)
          ) {
            facetValue = facetValue + ' ' + filterLabel;
          }
        }
        const active = bucket.count ? 'active' : '';
        const template = `
        <li id='${facet.title}-${bucket.value}' class='facets__item tw-flex-col tw-items-start facets__${facet.title}'>
          <label class='facets__label tw-group facet-checkbox ${active}' for='Filter-${facet.title}-${bucket.value}'>
            <input type='checkbox' id='Filter-${facet.title}-${bucket.value}' attribute='${facet.attribute}' class='facet-switcher-element-radio' value='${bucket.value}'>
            <span class='facet-checkbox__text group-[.active]:tw-border-b tw-link-menu' aria-hidden='true'>
              <span class='value' aria-hidden='true'>${facetValue}</span>
            </span>
          </label>
          </li>`;
        const parsedHTML = new DOMParser().parseFromString(template, 'text/html');
        parsedHTML.querySelector('input').addEventListener('click', (e) => this.applyFilter(e.target));
        clonedFacet.querySelector('fieldset ul').appendChild(parsedHTML.querySelector('li'));

      });
      template.querySelector('facets-switcher [data-facets]').appendChild(clonedFacet);
    });
    if (facetSelect && selectedValue) {
      template.querySelector('select').value = selectedValue
    }

    isNew && facetsWrapper.appendChild(template);
  }
  
  addBrandSearch() {
    const searchInput = `<div data-facet-search class='field tw-relative tw-mb-[1.6rem]'>
      <input
        class='search-input input__small search-vendor'
        type='text'
        placeholder='${Antonioli.searchStrings.search}...'
        title='${Antonioli.searchStrings.search}'>
        <span class='tw-absolute tw-right-[0.8rem] tw-top-1/2 tw-transform -tw-translate-y-1/2 [&_svg]:tw-w-[1.6rem] [&_svg]:tw-h-[1.6rem]'>
          <span class='icon-search'> </span>
        </span>
    </div>`;

    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(searchInput, 'text/html');

    parsedHTML.querySelector('input').addEventListener('input', (e) => {
      Array.from(document.querySelectorAll(`.facets__vendor`))
        .forEach(el => {
          const facetValue = el.querySelector('input').value.replaceAll(' ', '').toLowerCase();
          const inputValue = e.target.value.replaceAll(' ', '').toLowerCase();
          if (facetValue.startsWith(inputValue)) {
            el.classList.remove('hide');
          } else {
            el.classList.add('hide');
          }
        });
    });
    return parsedHTML.querySelector('div');

  }

  removeDetailsWithNoFacets(facets) {
    const attributes = facets.map(facet => `[data-search-filter="${facet.attribute}"]`);
    const sideBars = Array.from(document.querySelectorAll('.facets__disclosure-sidebar'));
    sideBars.forEach(sideBar => {
      const excludeIds = ['Details-Footwear', this.selectors.sortByDetails];
      if (!excludeIds.includes(sideBar.id) && !attributes.includes(sideBar.id)) {
        sideBar.remove();
      }
    });
  }

  handleDisableDepartments(departmentDetails) {
    const checkedInput = Array.from(departmentDetails.querySelectorAll('input')).find(el => el.checked);
    if (!checkedInput) return;
    Array.from(departmentDetails.querySelectorAll('.facet-checkbox')).forEach(element => {
      if (element.querySelector('input').id !== checkedInput.id) {
        element.classList.add('facet-radio--disabled');
      }
    });
  }

  createFacet(facet, bucket) {
    let facetId = `${facet.attribute}-${bucket.value}`;
    let facetValue = bucket.value;
    if (facet.attribute == this.getParamValue('categoryFirstLevel')) {
      facetId = `Filter-${bucket.value}`;
      facetValue = Antonioli.searchStrings.view_all;
    }
    if (facet.attribute == this.getParamValue('categorySecondLevel') || facet.attribute == this.getParamValue('categoryThirdLevel')) {
      facetId = `Filter-${bucket.value}`;
      facetValue = bucket.value.split('_')[1].toLowerCase();
    }

    const buttonChecked = bucket.count ? 'active' : '';
    const checked = bucket.count ? 'checked' : '';
    const clonedFacet = `<li class='facets__item tw-flex-col tw-items-start facets__${facet.attribute}' id='${facetId}'>
      <label
        class='facets__label tw-group facet-checkbox ${buttonChecked}'
        for='Filter-${facetId}'
      >
        <input
          attribute='${facet.attribute}'
          type='checkbox'
          id='Filter-${facetId}'
          value="${bucket.value}"
          ${checked}
        >
        <span class='facet-checkbox__text group-[.active]:tw-border-b tw-link-menu' aria-hidden='true'>
          <span class='value tw-capitalize'>${facetValue?.toLowerCase()}</span>
          <span class='content visually-hidden'> ( ${bucket.count} ) </span>
        </span>
      </label>
    </li>`;

    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(clonedFacet, 'text/html');

    parsedHTML.querySelector('input').addEventListener('click', (e) => this.applyFilter(e.target));
    return parsedHTML.querySelector('li');
  }

  createPriceFacet(facet, value, type) {
    const price = Shopify.formatMoney(value * 100, Antonioli.settings.moneyFormat);
    const filterID = type == 'from' ? 'GTE' : 'LTE'
    const clonedFacet = `<input
      class="field__input"
      name="${facet.attribute}-${filterID.toLowerCase()}"
      id="Filter-${facet.attribute}-${filterID}"
      type="number"
      placeholder="${price}"
      min="0"
      max="${value}"
      attribute='${facet.attribute}-${type}'
    >`;

    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(clonedFacet, 'text/html');
    parsedHTML.querySelector(`input[name="${facet.attribute}-${filterID.toLowerCase()}"]`).addEventListener('change', (e) => this.applyPriceFilter(e.target));

    return parsedHTML.querySelector('input');
  }
  
  handleProductTypeFacets(facets) {
    const facet = facets.find(facet => facet.attribute === 'product_type');
    const categoryAccordion = document.querySelector(this.selectors.productTypeDetails);

    const selectedCategory = Array.from(categoryAccordion?.querySelectorAll('.facets__item') || []).find(item => item.querySelector('input')?.checked);
    if (!selectedCategory) return;

    const list = document.createElement('ul');
    list.classList.add('facets__child-list tw-w-full tw-mt-[0.4rem]');

    facet?.buckets.forEach(bucket => {
      list.appendChild(this.createFacet(facet, bucket));
    });

    document.getElementById(selectedCategory.id).appendChild(list);
  }
  
  createDetailsTemplate(label, key, type = null) {
    const activeFacets = this.getCurrentFacetsObj(true);
    let counter = 0;
    Object.keys(activeFacets).forEach(k => {
      if (k == key) {
        counter = activeFacets[key].length;
      }
    });

    let template = document.querySelector(`[data-search-filter="${key}"]`);
    if (!template) {
      switch (type) {
        case 'dept':
          template = document.getElementById('facets-dept-details-template').content.cloneNode(true);
          template.querySelector('[facet-wrapper]').setAttribute('data-dept', true);
          break;
        case 'price':
          template = document.getElementById('facets-price-details-template').content.cloneNode(true);
          break;
        case 'size':
          template = document.getElementById('facets-size-details-template').content.cloneNode(true);
          break;
        default:
          template = document.getElementById('facets-details-template').content.cloneNode(true);
      }

      template.querySelector('[facet-wrapper]').setAttribute('data-search-filter', key);
      template.querySelector('[facet-wrapper]').setAttribute(`data-${type}`, true);
      template.querySelector('[data-facet-label]').innerHTML = label;
      template.querySelector('[data-facet-counter]').innerHTML = counter > 0 ? counter : '';
      template.querySelector('[data-facet-content]')?.classList.add('overflow-container');

      return [true, template];
    }

    template.querySelector('[data-facet-content]')?.replaceChildren();
    return [false, template];
  }

  renderSortBy(sort) {
    const sortByList = document.querySelector('[data-sortby]');
    sortByList.innerHTML = '';

    this.sortByProperties.forEach((sortBy) => {
      const sortByCenter = sortBy.alignCenter && 'tw-flex tw-justify-center'
      const isSelected = sort?.['sortBy'] === sortBy.value && sort?.['sortOrder'] === sortBy.order;
      const checked = isSelected ? 'checked' : '';

      const clonedFacet = `
      <li class='sort-option odd:tw-bg-grey0 even:tw-bg-white lg:odd:tw-bg-white lg:even:tw-bg-white lg:tw-link-menu'>
          <input
          class="tw-hidden"
          type="radio"
          value='${sortBy.value}'
          id='sb_${sortBy.id}'
          ${checked}
          name="sort_by"
        >
        </input>
        <label
        for='sb_${sortBy.id}'
        class="tw-px-[1.6rem] tw-py-[1.2rem] tw-w-full tw-block tw-whitespace-nowrap lg:tw-p-0 tw-cursor-pointer  ${sortByCenter}">
        ${sortBy.text}
        </label>
      </li>
    </li>`;
      const parsedHTML = new DOMParser().parseFromString(clonedFacet, 'text/html');
      sortByList.appendChild(parsedHTML.querySelector('li'));
      const checkbox = sortByList.querySelector(`#sb_${sortBy.id}`);
      checkbox.addEventListener('click', () => {
        this.applySort(sortBy.value, sortBy.order);
      });
    });

    /*     const facetsWrapper = document.getElementById('FacetsWrapperDesktop');
        facetsWrapper.appendChild(sortByList); */
  }


  handleAppliedFacets() {
    const facets = this.getCurrentFacetsObj(true);
    const removeAllFacets = this.activeFacetsContainer.querySelector('.active-facets-vertical-filter').cloneNode(true);

    let facetsPresent = false;
    Object.keys(facets).forEach(key => {
      if (key === 'meta.elastick.genderweb' || HIDDEN_DRAWER_FACETS.has(key)) return;
      if (facets[key].length) {
        facetsPresent = true;
      }
    });


    if (this.search.isMobile) {
     document.querySelector('.mobile-facets__buttons').classList.toggle('tw-hidden', !facetsPresent);
    }
    removeAllFacets.classList.toggle('!tw-hidden', !facetsPresent);
    
    this.activeFacetsContainer.innerHTML = '';
    this.activeFacetsContainer.appendChild(removeAllFacets);
    this.activeFacetsContainer.classList.remove('tw-mb-[2.4rem]')

    for (const [attribute, values] of Object.entries(facets)) {
      if (attribute === 'meta.elastick.genderweb' || HIDDEN_DRAWER_FACETS.has(attribute)) continue;
      const v = !Array.isArray(values[0]) ? [values[0]] : values[0];

      v.forEach(value => {
        const facetValue = value.includes('_') ? value.split('_')[1] : value
        const appliedFacet = `
        <div
          value='${value}'
          attribute='${attribute}'
          id='applied-facet-${attribute}-${value}'
          class="active-facets__button active-facets__button--light tw-leading-normal tw-cursor-pointer tw-bg-grey25 tw-mr-[0.8rem] tw-mt-[0.8rem]">
          <span class="active-facets__button-inner tw-pointer-events-none">
          ${facetValue.toLowerCase()}
            <span class="icon-close-s tw-ml-[0.5rem]"> </span>
          </span>
        </div>`;
        const parsedHTML = new DOMParser().parseFromString(appliedFacet, 'text/html');
        const appliedFacetEl = parsedHTML.querySelector('.active-facets__button');
        appliedFacetEl.addEventListener('click', (e) => this.applyFilter(e.target));
        this.activeFacetsContainer.appendChild(appliedFacetEl);
        this.activeFacetsContainer.classList.add('tw-mb-[2.4rem]')
      });
    }
  }
}

class SearchFiltersForm extends FacetFiltersForm  {
  constructor() {
    super();
    this.openFiltersToggle = this.querySelector('.facet-filters__toggle');
    this.openFilters();
  }


  openFilters() {
    if (this.openFiltersToggle) {
      this.openFiltersToggle.addEventListener('click', function () {
        document.querySelector('body').classList.toggle('open-filters');
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('search-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onSubmitForm(searchParams, event) {
    // Update sessionStorage
    let params = new URLSearchParams(searchParams);
    let grid = params.get("grid-switcher");
    sessionStorage.setItem('grid-switcher', grid);

    GridSwitcher.updateGridItems()
  }
}

customElements.define('search-filters-form', SearchFiltersForm);


class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.searchFilters = new SearchFilters();
    this.algoliaUtils = new AlgoliaUtils();

    this.loadedProducts = 0;
    this.ProductCountDesktop = document.getElementById('ProductCountDesktop')

    this.isMobile = new DeviceWidth().isMobile;
    this.searchWrapper = this.querySelector('.search-wrapper');
    this.searchTitle = this.querySelector('#main-search-title');
    this.searchProducts = this.querySelector('#product-grid-search');
    this.pagination = this.querySelector('.pagination__list') || false;

    this.loadPrevious = this.querySelector('[data-load-previous]');
    this.loadMore = this.querySelector('[data-load-more]');


    this.filters = {};
    this.sortBy = {};


    this.page = 0;
    this.perPage = 20;
    this.searchSequence = 0;

    this.setListeners();
  }

  clearFilters() {
    this.filters = {
      'meta.elastick.genderweb': [this.gender, "unisex"]
    };
    this.search();
  }

  setListeners() {
    subscribe(PUB_SUB_EVENTS.predictiveSearchUpdate, (event) => {
      this.query = (event.value || '').trim();
      if (this.query.length < SEARCH_MIN_CHARS) return;

      this.resetProps();
      if (event.gender) {
        this.gender = event.gender;
        this.filters['meta.elastick.genderweb'] = [event.gender, "unisex"];
      }
      this.search(true, true);
    });

    this.loadPrevious.addEventListener('click', () => {
      this.page--;
      this.search();
    });

    this.loadMore.addEventListener('click', async () => {
      this.page++;
      await this.search();
      this.loadPrevious.scrollIntoView({ behavior: 'smooth' });
    });
  }


  /**
   * When filter is present if it is macrocategory remove related microcategories
   * 
   * 
   */
  applyFilter(attribute, value) {
    this.page = 0;
  
    const adapter = {
      getAll: (attr) => this.filters[attr] ?? [],
      add: (attr, val) => {
        this.filters[attr] ??= [];
        this.filters[attr].push(val);
      },
      remove: (attr, val) => {
        this.filters[attr] = (this.filters[attr] || []).filter(v => v !== val);
        if (!this.filters[attr].length) delete this.filters[attr];
      },
      removeAll: (attr) => {
        delete this.filters[attr];
      },
      filterSelectors: this.searchFilters.selectors
    };
  
    this.algoliaUtils.manageFilter({ attribute, value, ...adapter });
  
    this.search();
  }

  resetProps() {
    this.loadedProducts = 0;
    this.page = 0;
    this.filters = [];
  }

  getCurrentFacetsObj(skipSort) {
    const currentFacets = {};
    const excluded = ['pagination', 'q', 'options[prefix]', 'tag', 'pb', '_fd', '_gl', 'gclid'];
    if (skipSort) {
      ['sortBy', 'sortOrder'].forEach(el => excluded.push(el));
    }

    for (const [key, value] of Object.entries(this.filters)) {
      if (!excluded.includes(key)) {
        if (!currentFacets[key]) {
          currentFacets[key] = [];
        }
        if (!Array.isArray(value) || value.length > 0) {
          currentFacets[key].push(value);
        }
      }
    }
    return currentFacets;
  }

  async getSearchProducts() {
    const appliedFacets = this.getCurrentFacetsObj();
    const { filters, facets } = this.algoliaUtils.getFacets(appliedFacets);

    this.searchTitle.innerHTML = `${this.query || ''}`;
    return this.algoliaUtils.getSearchProducts(this.query, filters, facets, this.sortBy, this.page, this.perPage);
  }

  applySort(value, order) {
    this.page = 0;
    this.sortBy['sortBy'] = value;
    this.sortBy['sortOrder'] = order;

    this.search();
  }

  /**
   * Runs the overlay search and renders it, ignoring responses that a newer
   * search has already superseded.
   *
   * @param {boolean} [clear=false] - Whether the facet list must be rebuilt.
   * @returns {Promise<void>}
   */
  async search(clear = false) {
    if ((this.query || '').trim().length < SEARCH_MIN_CHARS) return;

    const requestId = ++this.searchSequence;
    const results = await this.getSearchProducts();
    if (requestId !== this.searchSequence) return;

    const result = results[0];
    if (!result) return;

    const facets = this.algoliaUtils.mergeFilters(results, this.filters);

    if (result.nbHits === 0) {
      publish(PUB_SUB_EVENTS.predictiveSearchEmpty);
      return;
    }

    this.searchFilters.renderFacets(facets, this.sortBy, clear);

    this.loadedProducts = (this.perPage * this.page) + result.hits.length;
    this.ProductCountDesktop.innerHTML = `${result.nbHits}`;

    const resultHtml = await this.algoliaUtils.getGridProductsCard(result, {
      page: this.page,
      hitsPerPage: this.perPage,
      index: this.algoliaUtils.getIndexName(this.sortBy),
    });
    this.searchProducts.innerHTML = resultHtml;

    this.createPagination(result.nbHits);

    this.clearAllButton = this.querySelector('[data-clear-all]');
    this.clearAllButton?.addEventListener('click', 
        this.clearFilters.bind(this)
    );
  }

  createPagination(totalItems) {
    if (totalItems <= this.perPage) {
      this.loadPrevious.setAttribute('hidden', '');
      this.loadMore.setAttribute('hidden', '');
      return;
    }

    const pages = Math.ceil(totalItems / this.perPage);

    this.loadPrevious.toggleAttribute('hidden', this.page === 0);
    this.loadMore.toggleAttribute('hidden', this.page >= pages - 1);
  }

}

customElements.define('main-predictive-search', PredictiveSearch);



class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.searchFilters = new SearchFilters();
    this.algoliaUtils = new AlgoliaUtils();

    this.loadedProducts = 0;
    this.ProductCountDesktop = document.getElementById('ProductCountDesktop')
    const deviceWidth = new DeviceWidth();
    this.isMobile = deviceWidth.isMobile;
    this.searchWrapper = this.querySelector('.search-wrapper');
    this.searchTitle = this.querySelector('#main-search-title');
    this.searchProducts = this.querySelector('#product-grid');
    this.pagination = this.querySelector('.pagination__list') || false;


    this.perPage = Number(this.dataset.perPage) || 16;

    window.addEventListener('popstate', () => {
      this.search(true);
    });
    this.search(true);
  
    if (!this.isMobile) {
      this.searchTitle.addEventListener('click', this.handleClickSearch)
    }
  }


  handleClickSearch() {
    publish(PUB_SUB_EVENTS.clickSearchTitle)
  }

  clearFilters() {
    const parsedUrl = new URL(window.location.href);
    const queryParams = parsedUrl.searchParams;
    const filteredParams = Array.from(queryParams.keys())
      .filter(key => key === 'q')
      .reduce((acc, key) => {
        acc.append(key, queryParams.getAll(key));
        return acc;
      }, new URLSearchParams());

    parsedUrl.search = filteredParams.toString();
    history.replaceState(null, '', parsedUrl.toString());

    this.search();
  }

  resetProps() {
    this.loadedProducts = 0;
  }

  applyFilter(attribute, value) {
    this.resetProps();
    const currentUrl = new URL(location);
  
    const adapter = {
      getAll: (attr) => currentUrl.searchParams.getAll(attr),
      add: (attr, val) => currentUrl.searchParams.append(attr, val),
      remove: (attr, val) => currentUrl.searchParams.delete(attr, val),
      removeAll: (attr) => currentUrl.searchParams.delete(attr),
      filterSelectors: this.searchFilters.selectors

    };
  
    this.algoliaUtils.manageFilter({ attribute, value, ...adapter });
  
    history.pushState(null, null, currentUrl.toString());
    this.search(false);
  }
  

  getCurrentFacetsObj(skipSort) {
    const currentFacets = {};
    const excluded = ['pagination', 'q', 'options[prefix]', 'tag', 'pb', '_fd', '_gl', 'gclid'];
    if (skipSort) {
      ['sortBy', 'sortOrder'].forEach(el => excluded.push(el));
    }

    for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
      if (!excluded.includes(key)) {
        if (!currentFacets[key]) {
          currentFacets[key] = [];
        }
        currentFacets[key].push(value);
      }
    }
    return currentFacets;
  }

  async getSearchProducts() {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q') || '';
    const pagination = parseInt(params.get('pagination') || '1', 10);
    const page = pagination - 1;
    const sortBy = {};
    const sortByParam = params.get('sortBy');
    const sortOrderParam = params.get('sortOrder');
    if (sortByParam && sortOrderParam) {
      sortBy.sortBy = sortByParam;
      sortBy.sortOrder = sortOrderParam;
    }
    const appliedFacets = this.getCurrentFacetsObj(true);
    const { filters, facets } = this.algoliaUtils.getFacets(appliedFacets);
    this.searchTitle.innerHTML = `${query || ''}`;
    return this.algoliaUtils.getSearchProducts(query, filters, facets, sortBy, page, this.perPage);
  }

  async search(clear = false) {
    const query = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (query.length < SEARCH_MIN_CHARS) return;

    const results = await this.getSearchProducts();
    const result = results[0];
    if (!result) return;

    const appliedFacets = this.getCurrentFacetsObj(true);
    const facets = this.algoliaUtils.mergeFilters(results, appliedFacets);
    const params = new URLSearchParams(window.location.search);
    const sortBy = {};
    const sortByParam = params.get('sortBy');
    const sortOrderParam = params.get('sortOrder');
    if (sortByParam && sortOrderParam) {
      sortBy.sortBy = sortByParam;
      sortBy.sortOrder = sortOrderParam;
    }

    this.searchFilters.renderFacets(facets, sortBy, clear);
    this.createPagination(result.nbHits);

    const currentPage = parseInt(params.get('pagination') || '1', 10);

    this.loadedProducts = (this.perPage * (currentPage - 1)) + result.hits.length;
    this.ProductCountDesktop.innerHTML = `(${this.loadedProducts} of ${result.nbHits}) `;

    const resultHtml = await this.algoliaUtils.getGridProductsCard(result, {
      page: currentPage - 1,
      hitsPerPage: this.perPage,
      index: this.algoliaUtils.getIndexName(sortBy),
    });
    this.searchProducts.innerHTML = resultHtml;
  }

  createPagination(totalItems) {
    const urlParams = new URLSearchParams(window.location.search);
    // Fix: Default to 1 instead of 0 for 1-based pagination
    const currentPage = parseInt(urlParams.get('pagination') || '1', 10);
    const totalPages = Math.ceil(totalItems / this.perPage);
    this.pagination.innerHTML = '';
    
    if (totalPages <= 1) {
      return;
    }

    const svgArrow = this.createArrowSVG();
    const delta = 2; // Number of pages to show on each side of current page

    // Create previous arrow
    if (currentPage > 1) {
      this.createPaginationArrow('prev', currentPage - 1, svgArrow);
    }

    // Create page numbers
    this.createPageNumbers(currentPage, totalPages, delta);

    // Create next arrow
    if (currentPage < totalPages) {
      this.createPaginationArrow('next', currentPage + 1, svgArrow);
    }
  }

  createArrowSVG() {
    return `<svg class="icon icon-caret group-open/details:tw-rotate-180 tw-group/svg" xmlns="http://www.w3.org/2000/svg" width="13.685" height="12.544" viewBox="0 0 13.685 12.544">
    <path 
      id="Poligono_26" 
      class="group-open/details:tw-fill-black lg:group-hover/svg:tw-stroke-2" 
      data-name="Poligono 26" 
      d="M6,0l6,11H0Z" 
      transform="translate(12.842 11.5) rotate(180)" 
      fill="none" 
      stroke="#000" 
      stroke-width="1"/>
  </svg>`;
  }

  createPaginationArrow(direction, pageNumber, svgElement) {
    const li = document.createElement('li');
    const link = document.createElement('a');
    
    link.classList.add(
      'pagination__item',
      `pagination__item--${direction}`,
      'pagination__item-arrow',
      'link',
      'motion-reduce'
    );
    link.setAttribute('data-page', pageNumber);
    link.href = 'javascript:void(0)';
    link.setAttribute('aria-label', direction);
    link.innerHTML = svgElement;

    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleSearch(event);
    });

    li.appendChild(link);
    this.pagination.appendChild(li);
  }

  createPageNumbers(currentPage, totalPages, delta) {
    const leftBound = Math.max(1, currentPage - delta);
    const rightBound = Math.min(totalPages, currentPage + delta);
    let lastDisplayedPage = 0;

    for (let page = 1; page <= totalPages; page++) {
      const shouldShowPage = 
        page === 1 || 
        page === totalPages || 
        (page >= leftBound && page <= rightBound);

      if (!shouldShowPage) {
        continue;
      }

      // Add ellipsis if there's a gap
      if (lastDisplayedPage > 0 && page - lastDisplayedPage > 1) {
        if (page - lastDisplayedPage === 2) {
          // Show the middle page instead of ellipsis
          this.createPageLink(lastDisplayedPage + 1, currentPage, true);
          lastDisplayedPage = lastDisplayedPage + 1;
        } else {
          // Add ellipsis
          this.createEllipsis();
        }
      }

      this.createPageLink(page, currentPage, true);
      lastDisplayedPage = page;
    }
  }

  createPageLink(pageNumber, currentPage, isClickable) {
    const li = document.createElement('li');
    const link = document.createElement('a');
    
    link.setAttribute('data-page', pageNumber);

    if (pageNumber === currentPage) {
      link.classList.add('pagination__item', 'pagination__item--current', 'tw-font-medium');
      link.setAttribute('role', 'link');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('aria-current', 'page');
    } else {
      link.classList.add('pagination__item', 'link');
      if (isClickable) {
        link.href = 'javascript:void(0)';
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.handleSearch(event);
        });
      }
    }

    link.setAttribute('aria-label', `page ${pageNumber}`);
    link.innerHTML = pageNumber;

    li.appendChild(link);
    this.pagination.appendChild(li);
  }

  createEllipsis() {
    const li = document.createElement('li');
    const span = document.createElement('span');
    span.innerHTML = '...';
    li.appendChild(span);
    this.pagination.appendChild(li);
  }

  handleSearch(event) {
    const page = event.target.dataset.page || event.target.closest('a').dataset.page;

    const currentUrl = new URL(location);
    currentUrl.searchParams.set('pagination', page);
    history.pushState(null, null, currentUrl.toString());
    this.search();
  }
}

customElements.define('main-search', MainSearch);
